import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { MessageCircle, Send, X, Minus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const tb = { fontFamily: "'Tiro Bangla', serif" };

const LiveChat = () => {
    const { user } = useAuth();
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [text, setText] = useState('');
    const [sending, setSending] = useState(false);
    const [enabled, setEnabled] = useState(true);
    const [showPopup, setShowPopup] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const messagesEndRef = useRef(null);
    const popupTimerRef = useRef(null);

    const firstName = user?.name?.split(' ')[0] || 'Student';

    // Check if chat is enabled
    useEffect(() => {
        const checkEnabled = async () => {
            try {
                const res = await axios.get(`${API}/chat/settings`);
                setEnabled(res.data.enabled);
            } catch { /* default enabled */ }
        };
        checkEnabled();
    }, []);

    // Fetch messages
    const fetchMessages = useCallback(async () => {
        if (!user || user.role === 'teacher') return;
        try {
            const res = await axios.get(`${API}/chat/messages`);
            const msgs = res.data.messages || [];
            setMessages(msgs);
            const unread = msgs.filter(m => m.sender === 'admin' && !m.read).length;
            setUnreadCount(unread);
        } catch { /* silent */ }
    }, [user]);

    useEffect(() => {
        fetchMessages();
        const interval = setInterval(fetchMessages, open ? 3000 : 12000);
        return () => clearInterval(interval);
    }, [fetchMessages, open]);

    // Show popup after 30 seconds
    useEffect(() => {
        if (!enabled || !user) return;
        const shown = sessionStorage.getItem('chat_popup_shown');
        if (shown) return;
        popupTimerRef.current = setTimeout(() => {
            if (!open) {
                setShowPopup(true);
                sessionStorage.setItem('chat_popup_shown', '1');
                setTimeout(() => setShowPopup(false), 8000);
            }
        }, 30000);
        return () => clearTimeout(popupTimerRef.current);
    }, [enabled, user, open]);

    // Scroll to bottom
    useEffect(() => {
        if (open) messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, open]);

    // Mark read when opening
    useEffect(() => {
        if (open && unreadCount > 0) {
            axios.post(`${API}/chat/mark-read`).then(() => {
                setUnreadCount(0);
                fetchMessages();
            }).catch(() => {});
        }
    }, [open, unreadCount, fetchMessages]);

    const sendMessage = async () => {
        if (!text.trim() || sending) return;
        setSending(true);
        try {
            const res = await axios.post(`${API}/chat/send`, { text: text.trim() });
            setMessages(prev => [...prev, res.data]);
            setText('');
        } catch { /* silent */ }
        setSending(false);
    };

    if (!enabled || !user || user.role === 'teacher') return null;

    return (
        <>
            {/* Popup bubble */}
            {showPopup && !open && (
                <div 
                    className="fixed bottom-24 right-6 z-[9999] bg-white rounded-2xl shadow-2xl border border-slate-200 p-4 max-w-[260px] animate-in slide-in-from-bottom-4"
                    data-testid="chat-popup"
                >
                    <button onClick={() => setShowPopup(false)} className="absolute top-2 right-2 text-slate-400 hover:text-slate-600">
                        <X className="w-3.5 h-3.5" />
                    </button>
                    <p className="text-sm font-medium text-slate-800" style={tb}>
                        Hi {firstName}, কোনো সমস্যা?
                    </p>
                    <p className="text-xs text-slate-500 mt-1" style={tb}>Live chat-এ আমাদের সাথে কথা বলো!</p>
                    <button 
                        onClick={() => { setShowPopup(false); setOpen(true); }}
                        className="mt-3 w-full py-1.5 bg-indigo-600 text-white text-xs rounded-lg hover:bg-indigo-700 transition-colors"
                    >
                        Chat করো
                    </button>
                </div>
            )}

            {/* Chat window */}
            {open && (
                <div className="fixed bottom-24 right-6 z-[9999] w-[340px] h-[460px] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden animate-in slide-in-from-bottom-4" data-testid="chat-window">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-4 py-3 flex items-center justify-between">
                        <div>
                            <p className="text-white font-semibold text-sm">Bakkoshilpi Support</p>
                            <p className="text-indigo-200 text-[10px]">আমরা সাহায্য করতে এখানে আছি</p>
                        </div>
                        <button onClick={() => setOpen(false)} className="text-white/70 hover:text-white">
                            <Minus className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Messages */}
                    <div className="flex-1 overflow-y-auto p-3 space-y-2.5 bg-slate-50/50">
                        {messages.length === 0 && (
                            <div className="text-center py-12">
                                <MessageCircle className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                                <p className="text-xs text-slate-400" style={tb}>কোনো সমস্যা থাকলে এখানে লেখো!</p>
                            </div>
                        )}
                        {messages.map((msg) => (
                            <div key={msg.id} className={`flex ${msg.sender === 'student' ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm ${
                                    msg.sender === 'student'
                                        ? 'bg-indigo-600 text-white rounded-br-md'
                                        : 'bg-white text-slate-800 border border-slate-200 rounded-bl-md shadow-sm'
                                }`}>
                                    <p style={tb}>{msg.text}</p>
                                    <p className={`text-[9px] mt-1 ${msg.sender === 'student' ? 'text-indigo-200' : 'text-slate-400'}`}>
                                        {new Date(msg.created_at).toLocaleTimeString('bn-BD', { hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                </div>
                            </div>
                        ))}
                        <div ref={messagesEndRef} />
                    </div>

                    {/* Input */}
                    <div className="p-3 border-t bg-white">
                        <div className="flex items-center gap-2">
                            <input
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
                                placeholder="তোমার message লেখো..."
                                className="flex-1 text-sm border border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300"
                                data-testid="chat-input"
                            />
                            <button
                                onClick={sendMessage}
                                disabled={!text.trim() || sending}
                                className="w-9 h-9 rounded-xl bg-indigo-600 text-white flex items-center justify-center hover:bg-indigo-700 disabled:opacity-40 transition-colors"
                                data-testid="chat-send-btn"
                            >
                                <Send className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Floating button */}
            <button
                onClick={() => { setOpen(!open); setShowPopup(false); }}
                className="fixed bottom-6 right-6 z-[9999] w-14 h-14 rounded-full bg-gradient-to-br from-indigo-600 to-purple-600 text-white shadow-xl hover:shadow-2xl hover:scale-105 transition-all flex items-center justify-center"
                data-testid="chat-fab"
            >
                {open ? <X className="w-6 h-6" /> : <MessageCircle className="w-6 h-6" />}
                {!open && unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                        {unreadCount}
                    </span>
                )}
            </button>
        </>
    );
};

export default LiveChat;
